import React, { useState, useMemo } from 'react';
import {
  Button, Menu, MenuItem, ListSubheader, Divider, Box, TextField, Typography,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// Elegant, shared period picker. Emits { key, label, start, end } (YYYY-MM-DD),
// covering trailing windows, current/previous calendar periods, and a specific
// month or year. Used on the tenant Usage & Cost tab and the platform dashboard.

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function buildPresets(now = new Date()) {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const today = new Date(Date.UTC(y, m, now.getUTCDate()));
  // N-day window inclusive of today → start is (N-1) days back.
  const trailing = (days) => ({ start: ymd(new Date(today.getTime() - (days - 1) * 86400000)), end: ymd(today) });
  const q = Math.floor(m / 3);
  const pm = new Date(Date.UTC(y, m - 1, 1));        // previous month start
  const pmEnd = new Date(Date.UTC(y, m, 0));         // previous month end (day 0)
  const pqStart = new Date(Date.UTC(y, (q - 1) * 3, 1));
  const pqEnd = new Date(Date.UTC(y, q * 3, 0));
  return [
    { group: 'Trailing', items: [
      { key: 'last30', label: 'Last 30 days', ...trailing(30) },
      { key: 'last90', label: 'Last 90 days', ...trailing(90) },
      { key: 'last365', label: 'Last 365 days', ...trailing(365) },
    ] },
    { group: 'Current (to date)', items: [
      { key: 'this_month', label: `This month · ${MONTHS[m]} ${y}`, start: ymd(new Date(Date.UTC(y, m, 1))), end: ymd(today) },
      { key: 'this_quarter', label: `This quarter · Q${q + 1} ${y}`, start: ymd(new Date(Date.UTC(y, q * 3, 1))), end: ymd(today) },
      { key: 'this_year', label: `This year · ${y}`, start: ymd(new Date(Date.UTC(y, 0, 1))), end: ymd(today) },
    ] },
    { group: 'Previous', items: [
      { key: 'last_month', label: `Last month · ${MONTHS[pm.getUTCMonth()]} ${pm.getUTCFullYear()}`, start: ymd(pm), end: ymd(pmEnd) },
      { key: 'last_quarter', label: `Last quarter · Q${((q + 3) % 4) + 1}`, start: ymd(pqStart), end: ymd(pqEnd) },
      { key: 'last_year', label: `Last year · ${y - 1}`, start: `${y - 1}-01-01`, end: `${y - 1}-12-31` },
    ] },
  ];
}

function specificMonth(ym) {
  const [yy, mm] = ym.split('-').map(Number);
  return { key: `m:${ym}`, label: `${MONTHS[mm - 1]} ${yy}`, start: `${ym}-01`, end: ymd(new Date(Date.UTC(yy, mm, 0))) };
}
function specificYear(yy) {
  return { key: `y:${yy}`, label: `${yy}`, start: `${yy}-01-01`, end: `${yy}-12-31` };
}

// Default selection for parents to initialize with (Last 30 days).
export function initialPeriod() {
  return buildPresets()[0].items[0];
}

export default function PeriodSelector({ value, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const presets = useMemo(() => buildPresets(), []);
  const years = useMemo(() => {
    const y = new Date().getUTCFullYear();
    return [y, y - 1, y - 2, y - 3];
  }, []);
  // Specific-month selection: month + year dropdowns, current year by default.
  const [selMonth, setSelMonth] = useState('');
  const [selYear, setSelYear] = useState(() => new Date().getUTCFullYear());

  const pick = (v) => { onChange(v); setAnchor(null); };

  return (
    <>
      <Button
        variant="outlined" size="small"
        startIcon={<CalendarMonthIcon />} endIcon={<ArrowDropDownIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ textTransform: 'none', minWidth: 180, justifyContent: 'space-between' }}
      >
        {value?.label || 'Select period'}
      </Button>
      <Menu
        anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        disableAutoFocusItem
        slotProps={{ paper: { sx: { width: 300, maxHeight: 460 } } }}
      >
        {presets.map((grp) => ([
          <ListSubheader key={grp.group} disableSticky sx={{ lineHeight: '30px', bgcolor: 'transparent', fontWeight: 700 }}>
            {grp.group}
          </ListSubheader>,
          ...grp.items.map((it) => (
            <MenuItem key={it.key} selected={value?.key === it.key} onClick={() => pick(it)} sx={{ fontSize: 13 }}>
              {it.label}
            </MenuItem>
          )),
          <Divider key={`${grp.group}-d`} />,
        ]))}

        <ListSubheader disableSticky sx={{ lineHeight: '30px', bgcolor: 'transparent', fontWeight: 700 }}>Specific month</ListSubheader>
        <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            select size="small" label="Month" InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
            value={selMonth}
            onChange={(e) => setSelMonth(e.target.value)}
          >
            {MONTHS.map((mo, i) => <MenuItem key={mo} value={i + 1}>{mo}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Year" InputLabelProps={{ shrink: true }} sx={{ width: 92 }}
            value={selYear}
            onChange={(e) => setSelYear(e.target.value)}
          >
            {years.map((yy) => <MenuItem key={yy} value={yy}>{yy}</MenuItem>)}
          </TextField>
          <Button
            size="small" variant="contained" disabled={!selMonth || !selYear}
            onClick={() => pick(specificMonth(`${selYear}-${pad(selMonth)}`))}
          >
            Go
          </Button>
        </Box>

        <ListSubheader disableSticky sx={{ lineHeight: '30px', bgcolor: 'transparent', fontWeight: 700 }}>Specific year</ListSubheader>
        <Box sx={{ px: 2, pb: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {years.map((yy) => (
            <Button
              key={yy} size="small"
              variant={value?.key === `y:${yy}` ? 'contained' : 'outlined'}
              onClick={() => pick(specificYear(yy))}
            >
              {yy}
            </Button>
          ))}
        </Box>
        {value?.start ? (
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 1, display: 'block' }}>
            {value.start} → {value.end}
          </Typography>
        ) : null}
      </Menu>
    </>
  );
}
